import {AssetType, DocumentType, PartyRole, PayoutType} from '@sollapay/enums';

import {GeneralSaleSecondHandTrustType} from './general-sale-second-hand.trust-type';

import type {AmountDueParams} from './trust-type.strategy';
import type {Money} from '@sollapay/utils/money';

/** Default first — the toggle renders them in order. Holding two is also what makes
 * `asksSellerSideRole` true, so dropping one would silently stop the roster asking. */
const OPTION_SELLER_SIDE_ROLES: PartyRole[] = [PartyRole.SELLER, PartyRole.OPTION_HOLDER];

/** Land first so it's the default selection — the full 19-type list is noise for this deal type. */
const OPTION_ASSET_TYPES: AssetType[] = [AssetType.LAND, AssetType.OTHER];

/** סיחור אופציות — the land owner sells to an option holder, who assigns slices on to buyers. */
export class GeneralSaleOptionTrustType extends GeneralSaleSecondHandTrustType {
  // Extends second-hand, so both purchase flags must be explicitly reversed: an assignment deal is
  // N separate agreements over one shared parcel, each its own purchase, created as buyers arrive.
  override readonly createsPurchaseAtActivation: boolean = false;

  override readonly allowsMultiplePurchases: boolean = true;

  // The parcel is described once on the activation asset, so an assignment only needs its price and
  // its agreement — its asset row is generated rather than typed.
  override readonly collectsAssetPerPurchase: boolean = false;

  // Each assignment is one buyer group's own agreement — the same buyer twice is a duplicate, and
  // an assignment left with no buyers has nothing left to stand for.
  override readonly allowsBuyerOnMultiplePurchases: boolean = false;

  override readonly deletesPurchaseWithLastBuyer: boolean = true;

  // Lawyers track assignment tax reporting outside the platform.
  override readonly showsRegulatoryReports: boolean = false;

  /** Full price, not the outstanding balance second-hand returns — paid amounts never reduce it. */
  override computeAmountDue({maxCollectable}: AmountDueParams): Money {
    return maxCollectable;
  }

  /** Trustee instructions are collected per buyer instead — see `getOptionalPurchaseDocumentTypes`. */
  override getRequiredComplianceDocumentTypes(): DocumentType[] {
    return [DocumentType.LAND_OWNERSHIP_VERIFICATION];
  }

  /** הסכם סיחור — the assignment agreement, not the upstream option agreement. */
  override getRequiredPurchaseDocumentTypes(): DocumentType[] {
    return [DocumentType.ASSIGNMENT_AGREEMENT];
  }

  /** Per assignment, and optional — they're often an appendix of the assignment agreement. */
  override getOptionalPurchaseDocumentTypes(): DocumentType[] {
    return [DocumentType.TRUSTEE_INSTRUCTIONS];
  }

  override getSellerSideRoleOptions(): PartyRole[] {
    return OPTION_SELLER_SIDE_ROLES;
  }

  override getAllowedAssetTypes(): AssetType[] {
    return OPTION_ASSET_TYPES;
  }

  override getDefaultAssetType(): AssetType | null {
    return AssetType.LAND;
  }

  override getAssetPropertyDocumentTypes(): DocumentType[] {
    return [DocumentType.OPTION_AGREEMENT];
  }

  /** The underlying land-sale contract, offered alongside the option agreement but never required
   * — an assignment deal's own activation asset needn't have one on file. A distinct type from
   * `SIGNED_SALE_AGREEMENT`: at this stage the sale is not yet signed, so labelling it "signed"
   * would be wrong, and second-hand's own required document must stay untouched by an option
   * deal's optional upload. */
  override getOptionalAssetPropertyDocumentTypes(): DocumentType[] {
    return [DocumentType.SALE_AGREEMENT];
  }

  /** Inserted right after the developer/seller bank account, ahead of the rest of the second-hand list. */
  override getPayoutTypeOptions(): PayoutType[] {
    return super
      .getPayoutTypeOptions()
      .flatMap(option =>
        option === PayoutType.SELLER_BANK_ACCOUNT ? [option, PayoutType.OPTION_HOLDER] : [option]
      );
  }
}

export const generalSaleOptionTrustType = new GeneralSaleOptionTrustType();
